import { useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'

import { useGetSaltoConnectionStatus } from '@/api/salto/get-salto-connection-status'
import { SwitchFormField } from '@/components/form/switch-form-field'
import { selectActiveBuilding } from '@/reducers/buildingSelectorReducer'

import * as S from '../add-visitor-modal.styled'

type AccessBlockProps = {
  isRecurring: boolean
  isVendor: boolean
}

export const AccessBlock = ({ isRecurring, isVendor = false }: AccessBlockProps) => {
  const { t } = useTranslation(['visitors'])
  const building = useSelector(selectActiveBuilding)

  const resident = useWatch({ name: 'resident' })

  const saltoQuery = useGetSaltoConnectionStatus(resident)
  const isSaltoConnected = saltoQuery.data ?? false

  const brivoAvailable = building?.brivoAvailable
  const brivoAvailableVisitor = building?.brivoVisitorAccessAvailable
  const swiftlaneAvailable = building?.swiftlaneAvailable
  const swiftlaneVisitorAccessAvailable = building?.swiftlaneVisitorAccessAvailable
  const showPinCodeAccess =
    (swiftlaneAvailable && swiftlaneVisitorAccessAvailable) ||
    (brivoAvailable && brivoAvailableVisitor)

  const physicalKeyAvailable = building?.physicalKeyAvailable
  const customAccessAvailable = building?.customAccessAvailable
  const iglooWorkAvailable = building?.iglooWorkAvailable
  const iglooWorkVisitorAccessAvailable = building?.iglooWorkVisitorAccessAvailable

  const showKeylessViaSalto = isRecurring && !!resident && isSaltoConnected
  const showKeylessViaIgloo = iglooWorkAvailable && iglooWorkVisitorAccessAvailable
  const showKeyless = showKeylessViaSalto || showKeylessViaIgloo

  const hasBrivoAccess = brivoAvailable && brivoAvailableVisitor
  const hasAnyAccess =
    physicalKeyAvailable || customAccessAvailable || showKeyless || hasBrivoAccess

  if (!hasAnyAccess) return null

  return (
    <S.AccessRow>
      {physicalKeyAvailable && (
        <S.AccessItem>
          <S.AccessToggleRow>
            <S.LabelGroup>
              <S.NoteToggleLabel>
                {t('visitors:permissionAccess.physicalKeyRelease')}
              </S.NoteToggleLabel>
              <S.InlineTooltip placement="top">
                {t('visitors:dashboard.accessBlock.physicalKeyTooltip')}
              </S.InlineTooltip>
            </S.LabelGroup>
            {/* Note: As it was described in EL-14633 - keylessAccess is temporarly treated as physical key access,
            and keyAccess is treated as keyless integration */}
            <SwitchFormField name="keylessAccess" />
          </S.AccessToggleRow>
        </S.AccessItem>
      )}

      {customAccessAvailable && (
        <S.AccessItem>
          <S.AccessToggleRow>
            <S.LabelGroup>
              <S.NoteToggleLabel>
                {building?.customAccessName ||
                  t('visitors:dashboard.accessBlock.customAccessDefaultLabel')}
              </S.NoteToggleLabel>
              <S.InlineTooltip placement="top">
                {t('visitors:dashboard.accessBlock.customAccessTooltip')}
              </S.InlineTooltip>
            </S.LabelGroup>
            <SwitchFormField name="customAccess" />
          </S.AccessToggleRow>
        </S.AccessItem>
      )}

      {showKeyless && isVendor === false ? (
        <S.AccessItem>
          <S.AccessToggleRow>
            <S.LabelGroup>
              <S.NoteToggleLabel>{t('visitors:permissionAccess.keylessAccess')}</S.NoteToggleLabel>
              <S.InlineTooltip placement="top">
                {t('visitors:permissionAccess.keylessAccessDescription')}
              </S.InlineTooltip>
            </S.LabelGroup>
            {/* Note: As it was described in EL-14633 - keylessAccess is temporarly treated as physical key access,
            and keyAccess is treated as keyless integration */}
            <SwitchFormField name="keyAccess" />
          </S.AccessToggleRow>
        </S.AccessItem>
      ) : null}

      {showPinCodeAccess && isVendor === false ? (
        <S.AccessItem>
          <S.AccessToggleRow>
            <S.LabelGroup>
              <S.NoteToggleLabel>{t('visitors:permissionAccess.pinCodeAccess')}</S.NoteToggleLabel>
              <S.InlineTooltip placement="top">
                {t('visitors:permissionAccess.pinCodeDescription')}
              </S.InlineTooltip>
            </S.LabelGroup>
            <SwitchFormField name="pinCode" />
          </S.AccessToggleRow>
        </S.AccessItem>
      ) : null}
    </S.AccessRow>
  )
}
